import {
  OreButton,
  OreCardContainer,
  OreCardWrapper,
  OreDivider,
  OreHeading,
  OreInput,
  OreStack,
  OreText,
} from '@edenredespana/oreneta'
import { FormProvider } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { OtpLocationState } from '@/Account/domain'
import { createLoginRepository } from '@/Account/infrastructure/ApiLoginRepository'
import { LoginTranslation } from '@/Account/ui/accountTranslation'
import { useLoginController } from '@/Account/ui/hooks'
import { SignUpTranslation } from '@/AutoEnrollment/ui/autoEnrollmentTranslation'
import { SITE_INFO } from '@/Shared/constants/siteInfo'
import { HttpMethod } from '@/Shared/domain'
import { resendOtpCode } from '@/Shared/infrastructure/endpoints'
// Remove
import { useRateLimitedCountdown } from '@/Shared/ui/hooks'
import { navigationRoutes, openNavigationRoutes } from '@/Shared/ui/routers'
import { formTranslation } from '@/Shared/ui/sharedTranslation'

export const AuthOtp = (): JSX.Element => {
  const { t } = useTranslation()

  const location = useLocation() as { state: OtpLocationState }
  const origin = location.state?.origin
  const { otpForm, validateOtp, resendOtp } = useLoginController(
    createLoginRepository(),
    location.state?.otpGuid ?? '',
  )
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = otpForm

  const {
    formattedMessage,
    startCountdown,
    isBlocked,
    showCountdown,
    isFirstClick,
    setIsFirstClick,
  } = useRateLimitedCountdown({
    messageTemplate: t(formTranslation.common.retryBlockMessage),
    params: {
      endpointRoute: `/${resendOtpCode}`,
      endpointActionName: HttpMethod.POST,
    },
    needFakeCountdown: true,
  })

  const handleValidation = async (formData: { otp: string }): Promise<void> => {
    const response = await validateOtp(formData.otp)

    if (!response) {
      otpForm.setError('otp', {
        type: 'manual',
        message: t(formTranslation.errors.invalidCode),
      })
    } else if (origin === openNavigationRoutes.signUp) {
      globalThis.location.href = navigationRoutes.autoEnrollment
    } else {
      globalThis.location.href = navigationRoutes.home
    }
  }

  const handleOtpResend = async (): Promise<void> => {
    if (isFirstClick) {
      showCountdown()
      setIsFirstClick(false)
    } else {
      await resendOtp()
      startCountdown()
    }
  }

  return (
    <>
      {origin ? (
        <FormProvider {...otpForm}>
          <OreStack
            space="large"
            placeContent="stretch"
            placeItems="end"
            style={{ maxWidth: '50%' }}
          >
            <form onSubmit={handleSubmit(handleValidation)}>
              <OreCardContainer>
                <OreCardWrapper space="large">
                  <OreStack space="large" placeContent="stretch" placeItems="start">
                    <OreHeading as="h2" size="headline-lg">
                      {t(LoginTranslation.otp.title)}
                    </OreHeading>

                    <OreText size="text-md" tone="neutral-600">
                      <Trans
                        t={t}
                        components={[
                          <a href={`tel:${t(SignUpTranslation.otp.tel)}`} />,
                          <a href={`mailto:${SITE_INFO.contact.email}`} />,
                        ]}
                      >
                        {t(LoginTranslation.otp.subtitle, {
                          USER_EMAIL: location.state?.otpEmail ?? '',
                          CONTACT_PHONE: t(SignUpTranslation.otp.tel),
                          CONTACT_EMAIL: SITE_INFO.contact.email,
                        })}
                      </Trans>
                    </OreText>

                    <OreInput
                      {...register('otp')}
                      type="number"
                      label={t(LoginTranslation.otp.form.codeLabel)}
                      placeholder={t(SignUpTranslation.otp.code.placeholder)}
                      hasError={!!errors.otp}
                      errorMessage={errors.otp?.message}
                      required
                    />

                    <OreText>
                      {t(LoginTranslation.otp.resendCode)}{' '}
                      <OreButton
                        style={{
                          textDecoration: 'underline',
                          fontFamily: 'Ubuntu, sans-serif',
                          fontWeight: 'normal',
                        }}
                        onClick={(): void => {
                          handleOtpResend()
                        }}
                        disabled={isBlocked}
                      >
                        {t(LoginTranslation.otp.resendCodeLink)}
                      </OreButton>
                    </OreText>
                    {isBlocked && (
                      <OreStack placeItems="center" placeContent="center">
                        <OreText align="center" size="text-xs">
                          {formattedMessage}
                        </OreText>
                      </OreStack>
                    )}
                  </OreStack>
                </OreCardWrapper>
              </OreCardContainer>

              <OreDivider space="medium" />

              <OreStack space="large" placeContent="end" placeItems="end" direction="row">
                <OreButton
                  as="a"
                  type="submit"
                  category="secondary"
                  onClick={(event): void => {
                    event.preventDefault()
                    globalThis.location.href = openNavigationRoutes.login
                  }}
                >
                  {t(formTranslation.common.buttons.cancel)}
                </OreButton>
                <OreButton type="submit" category="primary" disabled={!isValid}>
                  {t(SignUpTranslation.otp.submit)}
                </OreButton>
              </OreStack>
            </form>
          </OreStack>
        </FormProvider>
      ) : (
        <p>404, Not Found</p>
      )}
    </>
  )
}
